// Module-level helper functions for assessment
import { TClass, TSubject } from "../../../apis/academic/academic.interfaces";
import {
  ACADEMIC_ASSESSMENT_STATUS_VALUES,
  ACADEMIC_ASSESSMENT_TYPE_VALUES,
  TAcademicAssessmentWireStatus,
  TAcademicAssessmentWireType,
} from "../../../apis/assessment/assessment.interfaces";

/**
 * Reads an id from OpenAPI nested `class` / `course` refs on assessments.
 */
export const academicAssessmentWireRefId = (
  ref: unknown,
): string | undefined => {
  if (!ref || typeof ref !== "object") return undefined;
  const o = ref as Record<string, unknown>;
  if (typeof o.id === "string" && o.id) return o.id;
  return undefined;
};

/** Map legacy API strings onto OpenAPI-aligned form enums. */
const ASSESSMENT_TYPE_ALIASES: Partial<
  Record<string, TAcademicAssessmentWireType>
> = {
  midterm: "mid_term",
};

/** Normalize status (e.g. legacy `active` → `in_progress`). */
const ASSESSMENT_STATUS_ALIASES: Partial<
  Record<string, TAcademicAssessmentWireStatus>
> = {
  active: "in_progress",
};

export const wireAssessmentTypeToFormValue = (
  wire: string,
): TAcademicAssessmentWireType => {
  const aliased = ASSESSMENT_TYPE_ALIASES[wire];
  if (aliased) return aliased;
  const set = ACADEMIC_ASSESSMENT_TYPE_VALUES as readonly string[];
  if (set.includes(wire)) return wire as TAcademicAssessmentWireType;
  return "exam";
};

export const wireAssessmentStatusToFormValue = (
  wire: string,
): TAcademicAssessmentWireStatus => {
  const aliased = ASSESSMENT_STATUS_ALIASES[wire];
  if (aliased) return aliased;
  const set = ACADEMIC_ASSESSMENT_STATUS_VALUES as readonly string[];
  if (set.includes(wire)) return wire as TAcademicAssessmentWireStatus;
  return "draft";
};

/**
 * Reads a display name from OpenAPI nested `class` / `course` objects on assessments.
 */
export const academicAssessmentWireRefName = (
  ref: unknown,
): string | undefined => {
  if (!ref || typeof ref !== "object") return undefined;
  const o = ref as Record<string, unknown>;
  if (typeof o.name === "string" && o.name) return o.name;
  return undefined;
};

/**
 * Class assessments need at least one class row and one institute subject (catalog).
 * Wire payloads still use the field name `courseId` for the subject FK — see BE §2.7.
 */
export const hasAssessmentPrerequisites = (
  classes: TClass[] | undefined,
  subjects: TSubject[] | undefined,
): boolean => {
  return !!(classes && classes.length > 0 && subjects && subjects.length > 0);
};
